import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { DocumentType } from 'support/types';

// State for the WebEDI packing-list / SSCC feature, scoped to a single document-editing session
// (reset in useInitWebEDIStore). It holds:
// - `enabled`: whether the packing-list capability is on for this document (synced from the server
//   capability by useCapabilities during the layout phase).
// - `existingUnitIdentifiers`: every SSCC `unit_identifier` the builder has seen this session (present
//   when the modal opens, and issued on apply). Real ones are drawn from a company's finite GS1 number
//   pool, so a new transport unit reuses an existing number no longer in use before requesting a fresh
//   one. The set only grows (by union); reuse is derived at generate time as existing minus in-use, so
//   no removal/reconciliation is needed. It survives the modal closing and reopening but never leaks
//   across documents.
// - `relationshipId` / `documentType`: the document context the SSCC-generation endpoint
//   (POST /v2/capabilities) resolves against.
type PackingListState = {
  enabled: boolean;
  existingUnitIdentifiers: Array<string>;
  relationshipId: string | undefined;
  documentType: DocumentType | undefined;
};

const initialState: PackingListState = {
  enabled: false,
  existingUnitIdentifiers: [],
  relationshipId: undefined,
  documentType: undefined,
};

export const usePackingListStore = create(immer<PackingListState>(() => ({ ...initialState })));

export const setPackingListEnabled = (enabled: boolean) => usePackingListStore.setState({ enabled });
export const getPackingListEnabled = () => usePackingListStore.getState().enabled;

export const addUnitIdentifiers = (unitIdentifiers: Array<string>) => {
  const current = usePackingListStore.getState().existingUnitIdentifiers;
  const next = Array.from(
    new Set([...current, ...unitIdentifiers.filter((identifier) => identifier.trim().length > 0)]),
  );
  usePackingListStore.setState({ existingUnitIdentifiers: next });
};

export const setPackingListContext = (context: {
  relationshipId: string | undefined;
  documentType: DocumentType | undefined;
}) => usePackingListStore.setState({ relationshipId: context.relationshipId, documentType: context.documentType });

export const resetPackingListStore = () => usePackingListStore.setState({ ...initialState });
